import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import {
    Switch,
    Route,
    Redirect
} from 'react-router-dom';
import Notifications from 'react-notification-system-redux';
import { isNullOrUndefined } from 'util';
import { noop } from 'lodash/fp';
import urlJoin from 'url-join';

import NavBar from '../webcommon/navbar/NavBar';
import TradesIcon from '../webcommon/common/Icons/Trades.svg';
import ImpactIcon from '../webcommon/common/Icons/Impact.svg';
import StaticIcon from '../webcommon/common/Icons/Static Data.svg';
import AdminIcon from '../webcommon/common/Icons/Admin.svg';
import RaboUser from '../webcommon/common/httpConnection/adfsUser';
import bioticSvg from '../webcommon/common/Icons/Biotic.svg';
import HelpIcon from '../webcommon/common/Icons/Help.svg';
import CommodityIcon from '../webcommon/common/Icons/Reconciliation.svg';
import UserIcon from '../webcommon/common/Icons/User.svg';
import CalculatorIcon from '../webcommon/common/Icons/Calculator2.svg';
import SettingsIcon from '../webcommon/common/Icons/Cog.svg';


import HelpInfo from './helpinfo/HelpInfo';
import Summary from '../summary/Summary';
import ImpactAnalysis from '../impactanalysis/ImpactAnalysis';
import StaticData from '../staticdata/StaticData';
import CommodityReport from '../commodityReport/commodityReport';
import IndependentAmount from '../independentAmount/independentAmountTab';


import Admin from '../admin/Admin';
import UserProfile from '../userProfile/Profile';
import Settings from '../settings/settingsTab';
import defaultStyles from './App.module.scss';
import { loaded } from './actions';
import * as selectors from './selectors';
import { AuthConsumer } from '../auth/AuthContext';
import * as permissions from '../auth/permissions';

import { getVirtualDirectory } from '../common/helpermethods/HelperMethods';

export const PAGE_ROUTE = urlJoin(getVirtualDirectory(), '/summary/csadrilldown');

function userName() {
    const raboEmail = RaboUser();
    if (!isNullOrUndefined(raboEmail)) {
        const names = raboEmail.split('.');
        if (names.length >= 2) { return `${names[0][0]}.${names[1]}`; }
        return names[0];
    }
    return 'Login';
}

const navbarItems = [
    {
        url: urlJoin(getVirtualDirectory(), '/summary'),
        title: 'Summary',
        icon: TradesIcon,
        component: Summary,
        requiredPermission: permissions.READ_CSA_SUMMARY,
        showAll: false
    },
    {
        url: urlJoin(getVirtualDirectory(), '/impactanalysis'),
        altUrl: urlJoin(getVirtualDirectory(), '/impactanalysis/:id?'),
        title: 'Impact Analysis',
        icon: ImpactIcon,
        component: ImpactAnalysis,
        requiredPermission: permissions.EXECUTE_IMPACT_ANALYSIS,
        showAll: false
    },
    {
        url: urlJoin(getVirtualDirectory(), '/staticdata'),
        title: 'Static Data',
        icon: StaticIcon,
        component: StaticData,
        requiredPermission: permissions.READ_STATIC_DATA,
        showAll: false
    },
    {
        url: urlJoin(getVirtualDirectory(), '/admin'),
        title: 'Admin',
        icon: AdminIcon,
        component: Admin,
        requiredPermission: permissions.EXECUTE_WEB_JOBS,
        showAll: false
    },    
    {
        url: urlJoin(getVirtualDirectory(), '/ia'),
        title: 'Independent Amount',
        icon: CalculatorIcon,
        component: IndependentAmount,
        requiredPermission: permissions.READ_CSA_SUMMARY,
        showAll: false
    },   
    {
        url: urlJoin(getVirtualDirectory(), '/commodityReport'),
        title: 'Commodity Future Report',
        icon: CommodityIcon,
        component: CommodityReport,
        requiredPermission: permissions.READ_STATIC_DATA,
        showAll: false
    },
    {
        url: urlJoin(getVirtualDirectory(), '/settings'),
        title: 'Settings ',
        icon: SettingsIcon,
        component: Settings,
        showAll: true
    }
];

const getDefaultRoute = (hasPermission) => {
    const orderedRoutes = [
        {
            permission: permissions.READ_CSA_SUMMARY,
            url: navbarItems[0].url
        },
        {
            permission: permissions.EXECUTE_IMPACT_ANALYSIS,
            url: navbarItems[1].url
        },
        {
            permission: permissions.READ_STATIC_DATA,
            url: navbarItems[2].url
        }
    ];
    for (let perm = 0; perm < orderedRoutes.length; perm++) {
        if (hasPermission(orderedRoutes[perm].permission)) {
            return orderedRoutes[perm].url;
        }
    }
    return navbarItems[4].url;
};

const App = ({ styles, ...props }) => {
    const { loaded } = props;
    useEffect(() => {
        loaded();
    }, [loaded]);

    return (
        props.initialised
            ? (
                <div className={ styles.App }>
                    <AuthConsumer>
                        { hasPermission => (
                            <>
                                <NavBar
                                    navItems={ navbarItems.filter(x => x.showAll
                                        || hasPermission(x.requiredPermission)) }
                                    appInfoItems={ [{
                                        title: 'Help',
                                        icon: HelpIcon,
                                        Component: <HelpInfo/>
                                    }] }
                                    titleIcon={ bioticSvg }
                                    title="Biotic"
                                />
                                <div className={ styles['content-area'] }>
                                    <Switch>
                                        {navbarItems
                                            .filter(x => x.showAll
                                                || hasPermission(x.requiredPermission))
                                            .map(x => (
                                                <Route
                                                    path={ x.altUrl || x.url }
                                                    component={ x.component }
                                                    key={ x.url }
                                                />
                                            ))}
                                        <Route render={ () => (
                                            <Redirect
                                                to={ getDefaultRoute(hasPermission) }
                                            />
                                        ) }
                                        />
                                    </Switch>
                                </div>
                            </>
                        ) }
                    </AuthConsumer>
                    <Notifications
                        notifications={ props.notifications }
                    />
                </div>
            )
            : null
    );
};

App.propTypes = {
    styles: PropTypes.objectOf(PropTypes.string),
    notifications: PropTypes.arrayOf(PropTypes.shape({})),
    loaded: PropTypes.func,
    initialised: PropTypes.bool.isRequired
};

App.defaultProps = {
    styles: defaultStyles,
    notifications: [],
    loaded: noop
};

export const mapStateToProps = state => ({
    notifications: state.notifications,
    initialised: selectors.getInitialised(state)
});

export const mapDispatchToProps = {
    loaded
};

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(App));
export { App as AppUnconnected };
